import React, { FC, useEffect, useRef, useState } from 'react'
import Radar from 'radar-sdk-js'
import './billing-address.css'
import 'radar-sdk-js/dist/radar.css'

// COMPONENTS
import TextField from '@components/atoms/textfield'

// TYPES
import type { Address } from '@stripe/stripe-js'
import type { RadarAddress } from 'radar-sdk-js/dist/types'

// UTILITIES
import { useInput } from '@hooks/inputs'
import { flatten } from '@utils/string-helper'


interface Props extends React.DetailedHTMLProps<React.FieldsetHTMLAttributes<HTMLFieldSetElement>, HTMLFieldSetElement> {
    initAddress?: Address
    onAddressChange?: (address?: Address, valid?: boolean) => void
}

const BillingAddressFields: FC<Props> = (props) => {
    const { className: classProps, children, initAddress, onAddressChange, ...fieldSetProps } = props
    const [ready, setReady] = useState(false)
    const ref = useRef<HTMLInputElement>(null)
    const className = flatten("billing-address", classProps)

    const [line1, line1Props, setLine1] = useInput({ initValue: initAddress?.line1 || '' })
    const [line2, line2Props] = useInput({ initValue: initAddress?.line2 || '' })
    const [city, cityProps, setCity] = useInput({ initValue: initAddress?.city || '' })
    const [state, stateProps, setState] = useInput({ initValue: initAddress?.state || '' })
    const [postalCode, postalCodeProps, setPostalCode] = useInput({ initValue: initAddress?.postal_code || '' })
    const [country, countryProps, setCountry] = useInput({ initValue: initAddress?.country || 'US' })

    useEffect(() => {
        if (onAddressChange) onAddressChange({
            city: city || null,
            country: country || null,
            line1: line1 || null,
            line2: line2 || null,
            postal_code: postalCode || null,
            state: state || null,
        }, (
            (!!line1 && !!city && !!state && !!country)
            && (postalCode.length === 5 && /^[0-9]*$/.test(postalCode))
        ))
    }, [line1, line2, city, state, postalCode, country])

    useEffect(() => {
        if (ready && !!ref.current) {
            Radar.initialize(import.meta.env.VITE_RADAR_PUB_KEY)
            Radar.ui.autocomplete({
                container: ref.current,
                showMarkers: false,
                onSelection: (address: RadarAddress) => {
                    // Do something with the selected address
                    console.log(address)
                    setLine1(address.addressLabel || line1)
                    setCity(address.city || city)
                    setState(address.stateCode || state)
                    setCountry(address.countryCode || country)
                    setPostalCode(address.postalCode || postalCode)
                },
            })
        } else setReady(true)
    }, [ready])

    return (
        <fieldset className={className} {...fieldSetProps}>
            <div><input ref={ref} {...line1Props} id="autocomplete" className="textfield" style={{ padding: '0.8rem 1.25rem' }} name="line1" placeholder="Address" autoComplete="off" required /></div>
            <TextField {...line2Props} name="line2" placeholder="Address 2" autoComplete="off" />
            <TextField {...cityProps} name="city" placeholder="City" autoComplete="off" required />
            <div className="input-row">
                <TextField {...stateProps} name="state" placeholder="State" autoComplete="off" required />
                <TextField {...postalCodeProps} type="text" name="postal_code" placeholder="Zip" autoComplete="off" minLength={5} maxLength={5} required />
            </div>
            <input {...countryProps} name="country" hidden />
            {children}
        </fieldset>
    )
}

export { BillingAddressFields }
